import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Heading, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuthGuard } from '../app/yowanAuthGuards';
import {
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

export const BLOG_AUTHOR_ROUTE_KEY = '/blog-author';

export const useBlogAuthorPageMetadata = (routeKey) => {
  const metadata = getYowanRouteMeta(routeKey);

  React.useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);
};

export const useBlogAuthorGuard = (guardOptions = {}) => {
  const location = useLocation();

  return useYowanAuthGuard({
    requiresAuth: true,
    requiresBlogAuthor: true,
    search: location.search,
    ...guardOptions,
  });
};

export const BlogAuthorGuardNotice = ({ notice }) => {
  useBlogAuthorPageMetadata(BLOG_AUTHOR_ROUTE_KEY);

  if (!notice) {
    return null;
  }

  return (
    <Section className="yowan-admin-page yowan-blog-author-page">
      <PageContainer>
        <div className="yowan-admin-form c-surface-content">
          <p className="yowan-admin-form__message">{notice.message}</p>
          {notice.actionHref ? (
            <p className="yowan-admin-form__actions">
              <Link to={notice.actionHref}>{notice.actionLabel || '移動する'}</Link>
            </p>
          ) : null}
        </div>
      </PageContainer>
    </Section>
  );
};

const useBlogAuthorShellState = () => {
  const shellRef = useRef(null);
  const resizingRef = useRef(false);
  const [compactSidebarMode, setCompactSidebarMode] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(232);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const syncSidebarState = (event) => {
      setCompactSidebarMode(event.matches);
      setSidebarHidden(event.matches);
    };

    syncSidebarState(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncSidebarState);
      return () => mediaQuery.removeEventListener('change', syncSidebarState);
    }

    mediaQuery.addListener(syncSidebarState);
    return () => mediaQuery.removeListener(syncSidebarState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (!resizingRef.current || !shellRef.current) {
        return;
      }

      const bounds = shellRef.current.getBoundingClientRect();
      const rawWidth = event.clientX - bounds.left;

      if (rawWidth <= 132) {
        setSidebarHidden(true);
        return;
      }

      const nextWidth = Math.min(Math.max(rawWidth, 168), 420);
      setSidebarWidth(nextWidth);
      setSidebarHidden(false);
    };

    const handlePointerUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  return {
    compactSidebarMode,
    resizingRef,
    shellRef,
    sidebarHidden,
    sidebarWidth,
    setSidebarHidden,
    setSidebarWidth,
  };
};

export const BlogAuthorShell = ({
  activeNavHref = '/blog-author',
  routeKey = '/blog-author',
  children,
}) => {
  const content = yowanPageContent['/blog-author'];
  const preservedDomainSearch = usePreservedDomainSearch();
  useBlogAuthorPageMetadata(routeKey);
  const {
    compactSidebarMode,
    resizingRef,
    shellRef,
    sidebarHidden,
    sidebarWidth,
    setSidebarHidden,
    setSidebarWidth,
  } = useBlogAuthorShellState();

  return (
    <Section className="yowan-admin-page yowan-blog-author-page">
      <PageContainer>
        <div
          ref={shellRef}
          className={`yowan-admin-shell yowan-stakeholder-shell yowan-blog-author-shell${sidebarHidden ? ' yowan-admin-shell--sidebar-hidden yowan-stakeholder-shell--sidebar-hidden yowan-blog-author-shell--sidebar-hidden' : ''}`}
          style={{
            '--admin-sidebar-width': `${sidebarWidth}px`,
            '--stakeholder-sidebar-width': `${sidebarWidth}px`,
          }}
        >
          <aside className="yowan-admin-shell__sidebar yowan-stakeholder-shell__sidebar yowan-blog-author-shell__sidebar">
            <div className="yowan-admin-sidebar yowan-stakeholder-sidebar yowan-blog-author-sidebar">
              {!sidebarHidden ? (
                <button
                  type="button"
                  className="yowan-admin-shell__toggle yowan-admin-shell__toggle--close"
                  aria-label="左ナビを隠す"
                  aria-expanded="true"
                  onClick={() => setSidebarHidden(true)}
                >
                  <span aria-hidden="true">‹</span>
                </button>
              ) : null}
              <Heading as="h1" align="left">
                <Link
                  className="yowan-admin-sidebar__home-link"
                  to={appendYowanPreviewQueryIfNeeded('/blog-author', preservedDomainSearch)}
                  aria-current={activeNavHref === '/blog-author' ? 'page' : undefined}
                >
                  {content.header.title}
                </Link>
              </Heading>
              <section className="yowan-admin-sidebar__group">
                <h2 className="yowan-admin-sidebar__group-title">ブログ</h2>
                <ul className="yowan-admin-sidebar__list">
                  {(content.navItems || []).map((item) => (
                    <li key={item.href}>
                      <Link
                        to={appendYowanPreviewQueryIfNeeded(item.href, preservedDomainSearch)}
                        aria-current={item.href === activeNavHref ? 'page' : undefined}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </aside>

          {sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__toggle yowan-admin-shell__toggle--open"
              aria-label="左ナビを表示"
              aria-expanded="false"
              onClick={() => {
                setSidebarWidth(Math.max(sidebarWidth, 208));
                setSidebarHidden(false);
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          ) : null}

          {!sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__resizer"
              aria-label="左ナビの幅を調整"
              onPointerDown={(event) => {
                event.preventDefault();
                resizingRef.current = true;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
            />
          ) : null}

          <main className="yowan-admin-shell__main yowan-stakeholder-shell__main">
            <div
              className="yowan-admin-shell__content yowan-admin-stack"
              onClick={() => {
                if (compactSidebarMode && !sidebarHidden) {
                  setSidebarHidden(true);
                }
              }}
            >
              {children}
            </div>
          </main>
        </div>
      </PageContainer>
    </Section>
  );
};

const BlogAuthorDashboardPage = ({ guardOptions }) => {
  const content = yowanPageContent['/blog-author'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useBlogAuthorGuard(guardOptions);

  if (guard.status !== 'ready') {
    return <BlogAuthorGuardNotice notice={guard.notice} />;
  }

  return (
    <BlogAuthorShell activeNavHref="/blog-author" routeKey="/blog-author">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
      />
      <div className="yowan-admin-link-groups c-surface-content">
        {(content.linkGroups || []).map((group) => (
          <section key={group.title} className="yowan-admin-link-groups__section">
            <Heading as="h2">{group.title}</Heading>
            <p className="yowan-admin-link-groups__lead">{group.body}</p>
            <ul className="yowan-admin-link-groups__list">
              {group.links.map((link) => (
                <li key={link.href}>
                  <Link to={appendYowanPreviewQueryIfNeeded(link.href, preservedDomainSearch)}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </BlogAuthorShell>
  );
};

export default BlogAuthorDashboardPage;
